import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const AboutPage = () => {
  const navigate = useNavigate();
  useScrollAnimation();

  const features = [
    { icon: 'fas fa-leaf', title: 'Natural Ingredients', text: 'Made with real fruit and full-cream milk.' },
    { icon: 'fas fa-award', title: 'MBS Certified', text: 'Approved by the Malawi Bureau of Standards for safety and quality.' },
    { icon: 'fas fa-industry', title: 'Modern Facility', text: 'Produced in an efficient and well-equipped plant in Lilongwe.' },
    { icon: 'fas fa-heart', title: 'Made for Families', text: 'Crafted to deliver great taste and nourishment every day.' }
  ];

  const yogurtFlavors = ['Banana', 'Strawberry', 'Pineapple', 'Orange', 'Marula'];

  // Navigation handler
  const handleNavigate = (path) => {
    navigate(`/${path}`);
  };

  return (
    <>
      {/* SEO Meta */}
      <Helmet>
        <title>About Riverton Dairy - Proudly Malawian Yogurt</title>
        <meta name="description" content="Learn about Riverton Dairy, producing high-quality yogurt in Malawi using real fruit and full-cream milk. Trusted by families nationwide." />
        <meta name="keywords" content="Riverton Dairy, Yogurt Malawi, Malawian Yogurt, About Riverton, MBS Certified, Flavored Yogurt" />
        <meta name="author" content="Riverton Dairy" />
        <meta property="og:title" content="About Riverton Dairy - Proudly Malawian Yogurt" />
        <meta property="og:description" content="Discover Riverton Dairy's story, values, and delicious yogurt flavors made with real fruit and full-cream milk in Malawi." />
        <meta property="og:image" content="/images/about/about_hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      {/* HERO */}
      <section className="about-hero scroll-animate" style={{ backgroundImage: `url('/images/about/about_hero.png')` }}>
        <div className="container">
          <div className="hero-content text-center">
            <h1>About Riverton Dairy</h1>
            <p className="hero-subtitle">It's Riverton, It's Delicious</p>
            <p className="hero-description">Proudly Malawian. Proudly Natural.</p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="story-section scroll-animate">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <h2>Our Story</h2>
              <p>
                <strong>Riverton Dairy Limited</strong>, a subsidiary of Madison Group, produces
                high-quality yogurt loved by families across Malawi. Every bottle is made with
                full-cream milk and real fruit flavour, giving Riverton its smooth texture and rich taste.
              </p>
              <p>
                Our flavoured yogurt range includes <strong>{yogurtFlavors.join(', ')}</strong>.
                These flavours were developed specifically for Malawian taste preferences and have become
                a favourite nationwide.
              </p>
              <p>
                Our production facility in Lilongwe is equipped with dependable processing and packaging
                systems, ensuring consistent freshness and quality from manufacturing to retail shelves.
              </p>

              <div className="story-highlights">
                <div className="highlight"><i className="fas fa-certificate"></i><span>MBS Certified</span></div>
                <div className="highlight"><i className="fas fa-seedling"></i><span>Real Fruit Ingredients</span></div>
                <div className="highlight"><i className="fas fa-truck"></i><span>Nationwide Availability</span></div>
              </div>
            </div>

            <div className="story-visual">
              <div className="visual-card">
                <div className="card-image" style={{ backgroundImage: `url('/images/about/about.jpg')` }}></div>
                <div className="card-badge"><i className="fas fa-industry"></i> Lilongwe Facility</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-section scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Riverton?</h2>
            <p>Trusted by families across Malawi</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon"><i className={feature.icon}></i></div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & VALUES */}
      <section className="purpose-section scroll-animate">
        <div className="container">
          <div className="purpose-cards">
            <div className="purpose-card vision-card">
              <div className="card-header"><i className="fas fa-eye"></i><h3>Our Vision</h3></div>
              <p>To become Malawi's most trusted yogurt brand through consistent quality and rich, satisfying flavour.</p>
              <p>We strive to build a culture centred on excellence, teamwork, and customer satisfaction.</p>
            </div>

            <div className="purpose-card mission-card">
              <div className="card-header"><i className="fas fa-bullseye"></i><h3>Our Values</h3></div>
              <p>We believe in producing yogurt that brings joy to Malawian families. Our commitment focuses on quality, responsibility, and continuous improvement in everything we do.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="products-preview scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Our Products</h2>
            <p>Flavours crafted for the Malawian palate</p>
          </div>
          <div className="products-grid">
            <div className="product-item">
              <div className="product-icon"><i className="fas fa-blender"></i></div>
              <h4>Flavoured Yogurt</h4>
              <p>{yogurtFlavors.join(', ')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION - FIXED */}
      <section className="about-cta scroll-animate">
        <div className="container">
          <div className="cta-content">
            <h2>Experience Real Flavour</h2>
            <p>Creamy. Refreshing. Proudly Malawian.</p>
            <div className="cta-buttons">
              <a onClick={() => handleNavigate('products')} className="btn btn-primary" style={{cursor: 'pointer'}}>
                Flavours & Products
              </a>
              <a onClick={() => handleNavigate('contact')} className="btn btn-secondary" style={{cursor: 'pointer'}}>
                Find Our Stores
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;